import { useEffect, useState, useContext } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { Visibility } from "@mui/icons-material";
import styles from "../Styles/loginPage.module.css";
import {
  Button,
  Paper,
  IconButton,
  InputAdornment,
  Typography,
  Grid,
} from "@mui/material";
import { postData } from "../Services/postData";
import { localUrlEnum, endpoints } from "../Utils/UrlEnum";
import { useNavigate } from "react-router-dom";
import { Vocabulary } from "../Utils/Vocabulary";
import { LoginModel } from "../Utils/Models";
import { LoginResponseType } from "../Utils/Types";
import { HttpStatusCode, PAYMENT_TYPES } from "../Utils/Constants";
import { WebSocketContext } from "../Context/WebSocketContext";

export default function Login() {
  const navigate = useNavigate();
  const { socket } = useContext(WebSocketContext);
  const [user, setUser] = useState<LoginModel>(new LoginModel());
  const [showPassword, setShowPassword] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      navigateAfterLogin();
    }
  }, [navigate]);

  /**
   * Reloads the page if currently on main route, otherwise navigates to main route
   */
  const navigateAfterLogin = () => {
    if (window.location.pathname == localUrlEnum.mainRoute)
      window.location.reload();

    navigate(localUrlEnum.mainRoute);
  };

  /**
   *
   */
  const handleSubmit = async () => {
    try {
      const response: LoginResponseType = await postData(endpoints.login, user);
      if (response && response?.data && response?.data?.userId) {
        // Check if subscription is expired
        if (response.data.subscriptionExpired) {
          const subscriptionData = response?.data?.subscriptionData;
          localStorage.setItem(
            "paymentType",
            PAYMENT_TYPES.SUBSCRIPTION_RENEWAL,
          );

          navigate(localUrlEnum.subscriptionExpired, {
            state: {
              clientId: response.data.userId,
              clientData: response.data.client,
              planName: subscriptionData?.name,
              expirationDate: subscriptionData?.expiredAt,
              renewalPrice: subscriptionData?.price,
              subscriptionId: subscriptionData?.subscriptionId,
            },
          });
          return;
        }

        // Store tokens and user data only if subscription is active
        localStorage.setItem("clientId", response.data.userId);
        localStorage.setItem("userName", response.data.userName);
        localStorage.setItem("access_token", response.data.access_token);
        localStorage.setItem("userRoles", JSON.stringify(response.data.roles));
        localStorage.setItem("fullName", response.data.fullName);
        localStorage.setItem("examCategory", response.data.examCategory);

        // Store flag to open password modal if needed
        // Treat missing/undefined/null as false (password not changed)
        const hasChangedPassword =
          (response.data as any).changedPassword === true;
        if (!hasChangedPassword) {
          localStorage.setItem("openPasswordModal", "true");
        }

        // Request storage data from WebSocket now that token is available
        socket.emit("storageData", {
          clientId: response.data.userId,
        });

        // Wait for WebSocket to populate storage data before navigating
        setTimeout(() => {
          navigateAfterLogin();
        }, 500);
      } else {
        setErrorText(Vocabulary.loginValidationError);
      }
    } catch (err: any) {
      if (err.response && err.response.status === HttpStatusCode.Unauthorized)
        setErrorText(Vocabulary.loginValidationError);
      else if (err.message) {
        console.error(err);
        setErrorText(err.message);
      }
    }
  };

  /**
   *
   * @param event
   */
  const onChangeTextField = (event: any) => {
    const newUser: any = Object.assign({}, user);
    newUser[event.target.name] = event.target.value;
    setUser(newUser);
  };

  return (
    <div className={styles.App}>
      <div className={styles.blurOverlay} />
      <Paper className={styles.loginContainer} elevation={5}>
        <Grid container spacing={1} className={styles.gridMainContainer}>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            className={styles.firstGrid}
          >
            <img
              src={"/sigur-la-volan-no-background.png"}
              alt="SigurLaVolan"
              width={"100%"}
            />
            <ValidatorForm className={styles.form} onSubmit={handleSubmit}>
              <TextValidator
                variant="standard"
                id="username"
                name="username"
                fullWidth
                label="Username"
                validators={["required"]}
                errorMessages={[Vocabulary.requiredField]}
                value={user.username}
                onChange={(e) => onChangeTextField(e)}
                className={styles.textfield}
              />
              <TextValidator
                variant="standard"
                id="password"
                name="password"
                type={!showPassword ? "password" : "text"}
                label={Vocabulary.password}
                fullWidth
                validators={["required"]}
                value={user.password}
                onChange={(e) => onChangeTextField(e)}
                className={styles.textfield}
                errorMessages={[Vocabulary.requiredField]}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        style={{ color: "#a5b3c0" }}
                        onClick={() => {
                          setShowPassword(!showPassword);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                type="submit"
                className={styles.submit}
              >
                {Vocabulary.login}
              </Button>
              <Typography className={styles.errorText}>{errorText}</Typography>
              <Button
                variant="outlined"
                type="reset"
                color="secondary"
                className={styles.reset}
                onClick={() => navigate(localUrlEnum.forgotPassword)}
              >
                {Vocabulary.forgotPassword}
              </Button>
            </ValidatorForm>
            {localStorage.getItem("no-cookie") !== "1" && (
              <div className={styles.registerLink}>
                <span onClick={() => navigate(localUrlEnum.clientRegistration)}>
                  {Vocabulary.dontHaveAccount}
                </span>
              </div>
            )}
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}
